import React from 'react';
import { Link } from 'react-router-dom';

import * as Yup from 'yup';

import { phoneValidator, simpleStringValidator } from '@common/react/utils/validationHelpers';
import FormikField from '@common/react/components/Forms/FormikField/FormikField';
import { FormikPhoneControl } from '@common/react/components/Forms/FormikPhoneControl/FormikPhoneControl';
import { ConfirmationCodeInput } from '@common/react/components/Forms/ConfirmationCodeInput/ConfirmationCodeInput';
import { RequestButton } from '@common/react/components/UI/LoadingButton/LoadingButton';
import { BaseParams } from '@common/react/objects/BaseParams';
import { ItemProvider } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { ItemEditor } from '@common/react/components/Core/ItemEditor/ItemEditor';
import { BaseUser } from '@common/typescript/objects/BaseUser';
import { BaseInit } from '@common/react/objects/BaseInit';
import { AuthResult } from '@common/react/objects/LoginFormValues';
import ItemModalProvider from '@common/react/components/Core/ItemModalProvider/ItemModalProvider';
import ExpandedLegend from '@common/react/components/UI/ExpandedLegend/ExpandedLegend';
import { useModal } from '@common/react/components/Modal/ModalContextProvider';
import { WithId } from '@common/typescript/objects/WithId';

interface Props<User extends BaseUser, TInit extends BaseInit<User>> {
	className?: string;
	finalAction?: (info: { phone?: string }, res: AuthResult<User, TInit>) => void;
	disabledConfirm?: boolean;
	sendConfirmPhoneRequestName?: string;
	sendConfirmPhoneParams?: BaseParams;
	phoneConfirmationCodeRequest?: string;
	sendPhoneConfirmationCodeRequest?: string;
	confirmText?: React.ReactNode;
	changeText?: React.ReactNode;
	defaultConfirmVisible?: boolean;
	modalOptions?: BaseParams;
	systemAdministrationLink?: string;
	onClickSystemAdministrationLink?: (e, setVisible) => void;
	savedPhoneNumber?: string;
	path?: string;
}

interface ChangeInfoFormik {
	id: number;
	phoneNumber: string;
}

const initialValues: ChangeInfoFormik = {
	id: -1,
	phoneNumber: '',
};

const validationSchema = Yup.object().shape({
	phoneNumber: phoneValidator,
});

const validationSchemaConfirm = Yup.object().shape({
	code: simpleStringValidator,
});

interface ConfirmPhoneCodeModal<User extends BaseUser, TInit extends BaseInit<User>> {
	phoneNumber: string;
	visibleConfirm: boolean;
	setVisibleConfirm: (visible: boolean) => void;
	modalOptions?: BaseParams;
	savedPhoneNumber?: string;
	phoneConfirmationCodeRequest?: string;
	afterSubmit?: (res: AuthResult<User, TInit>) => void;
	path?: string;
}

interface PhoneConfirmationCodeMessage extends WithId {
	path: string;
	code: string;
	phoneNumber: string;
	newPhoneNumber: string;
}

export const ConfirmPhoneCodeModal: <User extends BaseUser, TInit extends BaseInit<User> = BaseInit<User>>(
	props: ConfirmPhoneCodeModal<User, TInit>,
) => React.ReactElement<User> = <User extends BaseUser, TInit extends BaseInit<User> = BaseInit<User>>(
	props: ConfirmPhoneCodeModal<User, TInit>,
) => {
	const { renderModal } = useModal();
	const {
		modalOptions,
		savedPhoneNumber,
		visibleConfirm,
		setVisibleConfirm,
		phoneNumber,
		phoneConfirmationCodeRequest = 'phoneConfirmationCode',
		afterSubmit,
		path = '',
	} = props;
	return <>
		{renderModal(visibleConfirm, () => setVisibleConfirm(false), {
			title: 'Confirmation Code',
			destroyOnClose: true,
			width: 500,
			footer: null,
			...modalOptions,
			children: <>
				<div className="clearfix">
					<ExpandedLegend
						className="pull-left"
						showIcon
						type="info"
						style={{ marginBottom: 10 }}
						message={<div style={{ display: 'inline-block', marginLeft: 5 }}>
							An phone number confirmation message has been sent to your current phone number.
						</div>}
					/>
				</div>
				<ItemProvider<PhoneConfirmationCodeMessage, AuthResult<User, TInit>>
					id={-1}
					add={{
						path,
						code: '',
						...(savedPhoneNumber === phoneNumber ? { phoneNumber } : { newPhoneNumber: phoneNumber }),
					}}
					saveRequest={phoneConfirmationCodeRequest}
					validationSchema={validationSchemaConfirm}
				>
					<ItemEditor<PhoneConfirmationCodeMessage, AuthResult<User, TInit>>
						withButtons
						formikProps={{ enableReinitialize: true }}
						successMessage=""
						afterSubmit={({ res }) => {
							setVisibleConfirm(false);
							afterSubmit && afterSubmit(res);
						}}
						saveText="Confirm"
						edit={() => <div className="row">
							<FormikField
								containerClassName="col-sm-12 form-group mt10"
								fieldName="code"
								title=""
								render={(fieldProps) =>
									<div className="text-center">
										<label htmlFor="code">Code</label>
										<ConfirmationCodeInput
											fieldProps={fieldProps}
										/>
									</div>
								}
							/>
						</div>}
					/>
				</ItemProvider>
			</>,
		})}
	</>;
};

const ChangeInfoModal: <User extends BaseUser, TInit extends BaseInit<User> = BaseInit<User>>(
	props: Props<User, TInit>,
) => React.ReactElement<User> = <User extends BaseUser, TInit extends BaseInit<User> = BaseInit<User>>(
	props: Props<User, TInit>,
) => {
	const { openSuccessMessage } = useModal();
	const {
		className,
		finalAction = () => openSuccessMessage && openSuccessMessage('Success!'),
		disabledConfirm,
		sendConfirmPhoneParams = {},
		sendConfirmPhoneRequestName = 'sendPhoneConfirmationCode',
		phoneConfirmationCodeRequest = 'phoneConfirmationCode',
		sendPhoneConfirmationCodeRequest = 'sendPhoneConfirmationCode',
		confirmText = 'Confirm Phone',
		changeText = 'Change Phone',
		defaultConfirmVisible = false,
		modalOptions,
		systemAdministrationLink,
		onClickSystemAdministrationLink,
		savedPhoneNumber = '',
		path = '',
	} = props;
	const [visibleConfirm, setVisibleConfirm] = React.useState<boolean>(defaultConfirmVisible);
	const [phoneNumber, setPhoneNumber] = React.useState<string>(savedPhoneNumber);

	React.useEffect(() => {
		if (savedPhoneNumber !== phoneNumber) {
			setPhoneNumber(savedPhoneNumber);
		}
	}, [savedPhoneNumber]);

	return <div className={className ?? 'clearfix'}>
		<RequestButton
			requestType={sendConfirmPhoneRequestName}
			requestProps={sendConfirmPhoneParams}
			className="btn-group ratio-group btn-default btn"
			type="button"
			title="Confirm phone"
			disabled={!savedPhoneNumber || disabledConfirm}
			onSuccess={() => {
				setVisibleConfirm(true);
			}}
		>
			{confirmText}
		</RequestButton>
		<ItemModalProvider<ChangeInfoFormik>
			add={initialValues}
			loadRequest=""
			saveRequest={sendPhoneConfirmationCodeRequest}
			validationSchema={validationSchema}
			item={initialValues}
			getModalProps={() => ({
				title: 'Change Phone',
				destroyOnClose: true,
				footer: null,
				width: 500,
			})}
			resetAfterSave
			closeAfterSave
			render={(item, setOpen) => <>
				<div className="clearfix">
					<ExpandedLegend
						className="pull-left"
						showIcon
						type="info"
						style={{ marginBottom: 10 }}
						message={<div style={{ display: 'inline-block', marginLeft: 5 }}>
							You need access to your current phone to change it.
							<br />
							If you don't -
							{' '}
							{systemAdministrationLink ? <Link
								to={systemAdministrationLink}
								onClick={(e) => onClickSystemAdministrationLink?.(e, setOpen)}
							>
								Contact with support
							</Link> : 'Contact with support'}
						</div>}
					/>
				</div>
				<ItemEditor<ChangeInfoFormik>
					withButtons
					formikProps={{ enableReinitialize: true }}
					successMessage=""
					afterSubmit={({ values }) => {
						setPhoneNumber(values?.phoneNumber ?? '');
						setVisibleConfirm(true);
					}}
					saveText="Send confirmation Code"
					edit={() => <>
						<div className="row">
							<FormikField
								containerClassName="col-sm-12 form-group"
								fieldName="phoneNumber"
								title="New phone number"
								render={(fieldProps) => <>
									<FormikPhoneControl
										fieldProps={fieldProps}
										placeholder="+1 (___) ___-____"
									/>
								</>}
							/>
						</div>
					</>}
				/>
			</>}
		>
			{(modalContext) => <button
				title="Change Phone"
				className="btn-group ratio-group btn-default btn"
				type="button"
				disabled={!savedPhoneNumber}
				onClick={() => modalContext.actions.openModal(initialValues)}
			>
				{changeText}
			</button>}
		</ItemModalProvider>
		<ConfirmPhoneCodeModal<User, TInit>
			modalOptions={modalOptions}
			setVisibleConfirm={setVisibleConfirm}
			visibleConfirm={visibleConfirm}
			phoneNumber={phoneNumber}
			savedPhoneNumber={savedPhoneNumber}
			afterSubmit={(res) => {
				setVisibleConfirm(false);
				finalAction && finalAction({ phone: phoneNumber }, res);
			}}
			path={path}
			phoneConfirmationCodeRequest={phoneConfirmationCodeRequest}
		/>
	</div>;
};

export default ChangeInfoModal;
